import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { PageContent, PageSection, BodyText } from '../../../common/styles/page';

import ActionPlan from './OrganizationalGoalActionPlan';
import OrgAssignGoals from './OrganizationAssignGoals';
import SelectGoalCard from './SelectGoalCard';

import { 
  OrganizationAssessmentPeriodActionPlanRead,
} from '../../../store/generated/portal/assessments/plans-organization';

import { actionPlanApi } from 'areas/recommendations/api/actionPlanApi';
import { actionPlanTaskApi } from 'areas/recommendations/api/actionPlanTaskApi';

import { RecommendationType } from 'common/constants/RecommendationType';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { getCurrentOrganizationId, getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { GoalCardData } from '../interfaces/OrganizationGoalInterfaces';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';

const OrganizationalGoals = () => {
  const [selectedGoal, setSelectedGoal] = useState(0);
  
  const { setTopBarContent } = useContext(TopBarContext);
  const organizationId = useSelector(getCurrentOrganizationId);


  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: org_goal_data, isFetching: isFetchingGoals } =
    actionPlanApi.useApiPortalV1AssessmentsPlansOrganizationListQuery(
      {
        assessmentPeriodId: assessmentPeriodId,
        typeOf: RecommendationType.ORGANIZATIONAL_GOALS,
      },
      {
        skip: !assessmentPeriodId
      }
    );

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  const goalNavData = useMemo(() => {
    if (!org_goal_data || org_goal_data.length === 0) {
      return [];
    }

    return org_goal_data
      .slice()
      .sort((a, b) => {
        const targetA = a.recommendation_json?.target ?? 0;
        const targetB = b.recommendation_json?.target ?? 0;
        if (targetA !== targetB) {
          return targetA - targetB; // Primary sort: by target
        }
        const idA = a.id ?? 0;
        const idB = b.id ?? 0;
        return idA - idB; // Secondary sort: by id to maintain tab order in case of equal targets
      })
      .map((item: OrganizationAssessmentPeriodActionPlanRead) => ({
        key: item.id,
        completion: 100,
        header: item.recommendation_json?.title || 'No title',
        bodyText: item.recommendation_json?.description || 'No description',
        context: item.recommendation_json?.context || {},
        details: item.recommendation_json?.details || '',
        target: item.recommendation_json?.target || 1,
      }));
  }, [org_goal_data]);

  const orgGoalIds = useMemo(() => goalNavData.map(goal => goal.key), [goalNavData]);

  const activeGoalId = selectedGoal || goalNavData[0]?.key || 0;

  const {
    data: task_data,
    isFetching: isFetchingTasks,
    isLoading: isLoadingTasks,
  } = actionPlanTaskApi.useApiPortalV1AssessmentsPlansTasksListQuery(
    {
      assessmentPeriodActionPlanId: activeGoalId,
    },
    {
      skip: !activeGoalId,
      refetchOnMountOrArgChange: true,
    }
  );

  const taskData = task_data ?? [];

  const isLoading =
    isFetchingGoals ||
    (!org_goal_data && assessmentPeriodId) ||
    (!!activeGoalId && (isFetchingTasks || isLoadingTasks));

  if (isLoading) return <LoadingSpinner />;

  if (!assessmentPeriodId) {
    return (
      <PageContent>
        <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 360 }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>No Closed Assessment Periods</h3>
            <p>Organizational goals can be selected when an assessment period is completed.</p>
          </div>
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent>
      {goalNavData.length === 0 ? (
        <PageSection>
          <OrgAssignGoals />
        </PageSection>
      ) : (
        <PageSection padbottom>
          <SelectGoalCard
            goalCardData={goalNavData}
            setSelectedGoal={setSelectedGoal}
            activeGoalId={activeGoalId}
          />
          <PageSection>
            {taskData.length === 0 ? (
              <BodyText>
                No action plan tasks are available for the selected goal yet.
              </BodyText>
            ) : (
              <ActionPlan taskData={taskData} orgGoalIds={orgGoalIds} />
            )}
          </PageSection>
        </PageSection>
      )}
    </PageContent>
  );
};

export default OrganizationalGoals;
